import React, { useState, useRef, useEffect } from "react";
import { Card, Row, Col, Spin, Alert, Pagination } from "antd";
import BannerCarousel from "../components/BannerCarousel";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import "../styles/Home.css";
import { Contact } from "../components/Contact";
import "../styles/animations.css";
import { noticiasAPI, perguntasAPI } from "../services/api";
import { useTranslation } from "react-i18next";

// Definição de constantes para paginação
const ITEMS_PER_PAGE = 6;

export const Recursos = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("csap");
  const [activeFaq, setActiveFaq] = useState(null);
  const [activeNewsAccordion, setActiveNewsAccordion] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const sectionRef = useRef(null);
  const faqSectionRef = useRef(null);

  // Estados para notícias
  const [noticiasRHA, setNoticiasRHA] = useState([]);
  const [noticiasCSAP, setNoticiasCSAP] = useState([]);
  const [loadingNoticiasRHA, setLoadingNoticiasRHA] = useState(false);
  const [loadingNoticiasCSAP, setLoadingNoticiasCSAP] = useState(false);
  const [errorNoticiasRHA, setErrorNoticiasRHA] = useState(null);
  const [errorNoticiasCSAP, setErrorNoticiasCSAP] = useState(null);

  // Estados para perguntas frequentes separados por especialidade
  const [perguntasRHA, setPerguntasRHA] = useState([]);
  const [perguntasCSAP, setPerguntasCSAP] = useState([]);
  const [loadingPerguntasRHA, setLoadingPerguntasRHA] = useState(false);
  const [loadingPerguntasCSAP, setLoadingPerguntasCSAP] = useState(false);
  const [errorPerguntasRHA, setErrorPerguntasRHA] = useState(null);
  const [errorPerguntasCSAP, setErrorPerguntasCSAP] = useState(null);

  // Estados para paginação
  const [currentPageRHA, setCurrentPageRHA] = useState(1);
  const [currentPageCSAP, setCurrentPageCSAP] = useState(1);

  React.useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;
    section.classList.add("hidden");
    const observer = new window.IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          section.classList.add("fade-up");
          section.classList.remove("hidden");
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    observer.observe(section);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const section = faqSectionRef.current;
    if (!section) return;
    section.classList.add("hidden");
    const observer = new window.IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          section.classList.add("fade-up");
          section.classList.remove("hidden");
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    observer.observe(section);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    fetchNoticiasRHA();
    fetchNoticiasCSAP();
    fetchPerguntasRHA();
    fetchPerguntasCSAP();

    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const fetchNoticiasRHA = async () => {
    setLoadingNoticiasRHA(true);
    try {
      const response = await noticiasAPI.getAll("RHA");
      setNoticiasRHA(response.data);
      setErrorNoticiasRHA(null);
    } catch (err) {
      console.error("Erro ao carregar notícias RHA:", err);
      setErrorNoticiasRHA(t("resources.errors.loadNews"));
    } finally {
      setLoadingNoticiasRHA(false);
    }
  };

  const fetchNoticiasCSAP = async () => {
    setLoadingNoticiasCSAP(true);
    try {
      const response = await noticiasAPI.getAll("CSAP");
      setNoticiasCSAP(response.data);
      setErrorNoticiasCSAP(null);
    } catch (err) {
      console.error("Erro ao carregar notícias CSAP:", err);
      setErrorNoticiasCSAP(t("resources.errors.loadNews"));
    } finally {
      setLoadingNoticiasCSAP(false);
    }
  };

  const fetchPerguntasRHA = async () => {
    setLoadingPerguntasRHA(true);
    try {
      const response = await perguntasAPI.getAll("RHA");
      setPerguntasRHA(response.data);
      setErrorPerguntasRHA(null);
    } catch (err) {
      setErrorPerguntasRHA(t("resources.errors.loadFaq"));
    } finally {
      setLoadingPerguntasRHA(false);
    }
  };

  const fetchPerguntasCSAP = async () => {
    setLoadingPerguntasCSAP(true);
    try {
      const response = await perguntasAPI.getAll("CSAP");
      setPerguntasCSAP(response.data);
      setErrorPerguntasCSAP(null);
    } catch (err) {
      setErrorPerguntasCSAP(t("resources.errors.loadFaq"));
    } finally {
      setLoadingPerguntasCSAP(false);
    }
  };

  // Banner para a página Recursos
  const banner = [
    {
      subtitle: t("resources.banner.subtitle"),
      description: t("resources.banner.description"),
      image: "/assets/img/banners/banner-geral.png",
      buttonText: t("resources.banner.buttonText"),
    },
  ];

  // Fallback para notícias estáticas caso a API falhe - RHA
  const noticiasRHAEstaticas = [];
  // Fallback para notícias estáticas caso a API falhe - CSAP
  const noticiasCSAPEstaticas = [];

  // Fallback para perguntas frequentes estáticas caso a API falhe
  const faqDataEstaticas = [];

  // Funções de formatação
  const formatNoticias = (data) => {
    return data.map((noticia) => ({
      id: noticia._id,
      title:
        noticia.titulo.length > 70
          ? noticia.titulo.substring(0, 70) + "..."
          : noticia.titulo,
      description:
        noticia.texto.substring(0, 150) +
        (noticia.texto.length > 150 ? "..." : ""),
      image:
        noticia.imagem && noticia.imagem.startsWith("http")
          ? noticia.imagem
          : `${
              process.env.REACT_APP_API_URL.replace(/\/api\/?$/, "") ||
              "http://localhost:5000"
            }${noticia.imagem}`,
      data: new Date(noticia.createdAt).toLocaleDateString("pt-BR"),
      categoria: noticia.categoria || "Geral",
      link: noticia.link || "#",
    }));
  };

  const formatPerguntas = (data) => {
    return data.map((pergunta) => ({
      id: pergunta._id || pergunta.id,
      pergunta: pergunta.pergunta,
      resposta: pergunta.resposta,
    }));
  };

  // Determinar quais dados mostrar
  const noticiasRHAParaMostrar =
    noticiasRHA.length > 0 ? formatNoticias(noticiasRHA) : noticiasRHAEstaticas;
  const noticiasCSAPParaMostrar =
    noticiasCSAP.length > 0
      ? formatNoticias(noticiasCSAP)
      : noticiasCSAPEstaticas;
  const perguntasRHAParaMostrar =
    perguntasRHA.length > 0
      ? formatPerguntas(perguntasRHA)
      : faqDataEstaticas.filter((f) => !f.produto || f.produto === "RHA");
  const perguntasCSAPParaMostrar =
    perguntasCSAP.length > 0
      ? formatPerguntas(perguntasCSAP)
      : faqDataEstaticas.filter((f) => !f.produto || f.produto === "CSAP");

  // Lógica para paginação (apenas na versão desktop)
  const startIndexRHA = (currentPageRHA - 1) * ITEMS_PER_PAGE;
  const endIndexRHA = startIndexRHA + ITEMS_PER_PAGE;

  const startIndexCSAP = (currentPageCSAP - 1) * ITEMS_PER_PAGE;
  const endIndexCSAP = startIndexCSAP + ITEMS_PER_PAGE;

  // Função para lidar com a mudança de página
  const handlePageChangeRHA = (page) => {
    setCurrentPageRHA(page);
    window.scrollTo({
      top: sectionRef.current.offsetTop - 100,
      behavior: "smooth",
    });
  };

  const handlePageChangeCSAP = (page) => {
    setCurrentPageCSAP(page);
    window.scrollTo({
      top: sectionRef.current.offsetTop - 100,
      behavior: "smooth",
    });
  };

  // Itens a serem exibidos na página atual (versão desktop)
  const paginatedNoticiasRHA = isMobile
    ? noticiasRHAParaMostrar
    : noticiasRHAParaMostrar.slice(startIndexRHA, endIndexRHA);

  const paginatedNoticiasCSAP = isMobile
    ? noticiasCSAPParaMostrar
    : noticiasCSAPParaMostrar.slice(startIndexCSAP, endIndexCSAP);

  // Novo estado para controlar o tipo de FAQ ativo no mobile
  const [activeMobileFaq, setActiveMobileFaq] = useState("csap");

  return (
    <div className="recursos">
      <Navbar />
      <div id="banner-recursos">
        <BannerCarousel banners={banner} />
      </div>
      {/* Seção Principal de Recursos */}
      <section id="recursos-principais" className="" ref={sectionRef}>
        <div className="container">
          <div className="">
            <div className="plataforma-header">
              <h2>{t("resources.news.title")}</h2>
              <p>{t("resources.news.subtitle")}</p>
            </div>
            <Card className="plataforma-tabs-card" style={{ margin: 0 }}>
              {isMobile ? (
                <div className="faq-container">
                  {/* Accordion para CSAP */}
                  <div
                    className={`faq-item ${
                      activeNewsAccordion === "csap" ? "active" : ""
                    }`}
                  >
                    <div
                      className="faq-question"
                      onClick={() => {
                        const next =
                          activeNewsAccordion === "csap" ? null : "csap";
                        setActiveNewsAccordion(next);
                        if (next) setActiveMobileFaq("csap");
                      }}
                    >
                      <h4>{t("resources.tabs.csap")}</h4>
                      <div className="faq-icon">
                        <span
                          className={`faq-arrow ${
                            activeNewsAccordion === "csap" ? "rotated" : ""
                          }`}
                        >
                          ▼
                        </span>
                      </div>
                    </div>
                    <div
                      className={`faq-answer ${
                        activeNewsAccordion === "csap" ? "expanded" : ""
                      }`}
                    >
                      <div className="faq-answer-content">
                        {/* ...noticias CSAP... */}
                        <div className="noticias-header">
                          <h3>{t("resources.news.csap.title")}</h3>
                          <p>{t("resources.news.csap.subtitle")}</p>
                        </div>
                        {loadingNoticiasCSAP ? (
                          <div className="loading-container">
                            <Spin
                              size="large"
                              tip={t("resources.loading.news")}
                            />
                          </div>
                        ) : errorNoticiasCSAP ? (
                          <Alert
                            message={errorNoticiasCSAP}
                            type="error"
                            showIcon
                          />
                        ) : (
                          <Row gutter={[24, 24]} className="noticias-grid">
                            {paginatedNoticiasCSAP.map((noticia) => (
                              <Col xs={24} key={noticia.id}>
                                <div className="noticia-card">
                                  <div className="noticia-image">
                                    <img
                                      src={noticia.image}
                                      alt={noticia.title}
                                      crossOrigin="anonymous"
                                    />
                                    {/* <div className="noticia-categoria">{noticia.categoria}</div> */}
                                  </div>
                                  <div className="noticia-content">
                                    <div className="noticia-data">
                                      {noticia.data}
                                    </div>
                                    <h4 className="noticia-title">
                                      {noticia.title}
                                    </h4>
                                    <p className="noticia-description">
                                      {noticia.description}
                                    </p>
                                    <a
                                      href={noticia.link}
                                      className="noticia-link"
                                    >
                                      {t("resources.news.readMore")} →
                                    </a>
                                  </div>
                                </div>
                              </Col>
                            ))}{" "}
                          </Row>
                        )}
                        {/* FAQ para CSAP no mobile */}
                        {activeNewsAccordion === "csap" && (
                          <div className="faq-mobile-section">
                            <h3 style={{ marginTop: 24 }}>
                              {t("resources.faq.title")}
                            </h3>
                            {loadingPerguntasCSAP ? (
                              <div className="loading-container">
                                <Spin
                                  size="large"
                                  tip={t("resources.loading.faq")}
                                />
                              </div>
                            ) : errorPerguntasCSAP ? (
                              <Alert
                                message={errorPerguntasCSAP}
                                type="error"
                                showIcon
                              />
                            ) : (
                              <div className="faq-container">
                                {perguntasCSAPParaMostrar.map((faq) => (
                                  <div
                                    key={faq.id}
                                    className={`faq-item ${
                                      activeFaq === faq.id ? "active" : ""
                                    }`}
                                  >
                                    <div
                                      className="faq-question"
                                      onClick={() =>
                                        setActiveFaq(
                                          activeFaq === faq.id ? null : faq.id
                                        )
                                      }
                                    >
                                      <h4>{faq.pergunta}</h4>
                                      <div className="faq-icon">
                                        <span
                                          className={`faq-arrow ${
                                            activeFaq === faq.id
                                              ? "rotated"
                                              : ""
                                          }`}
                                        >
                                          ▼
                                        </span>
                                      </div>
                                    </div>
                                    <div
                                      className={`faq-answer ${
                                        activeFaq === faq.id ? "expanded" : ""
                                      }`}
                                    >
                                      <div className="faq-answer-content">
                                        <p>{faq.resposta}</p>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  {/* Accordion para RHA */}
                  <div
                    className={`faq-item ${
                      activeNewsAccordion === "rha" ? "active" : ""
                    }`}
                  >
                    <div
                      className="faq-question"
                      onClick={() => {
                        const next =
                          activeNewsAccordion === "rha" ? null : "rha";
                        setActiveNewsAccordion(next);
                        if (next) setActiveMobileFaq("rha");
                      }}
                    >
                      <h4>{t("resources.tabs.rha")}</h4>
                      <div className="faq-icon">
                        <span
                          className={`faq-arrow ${
                            activeNewsAccordion === "rha" ? "rotated" : ""
                          }`}
                        >
                          ▼
                        </span>
                      </div>
                    </div>
                    <div
                      className={`faq-answer ${
                        activeNewsAccordion === "rha" ? "expanded" : ""
                      }`}
                    >
                      <div className="faq-answer-content">
                        {/* ...noticias RHA... */}
                        <div className="noticias-header">
                          <h3>{t("resources.news.rha.title")}</h3>
                          <p>{t("resources.news.rha.subtitle")}</p>
                        </div>
                        {loadingNoticiasRHA ? (
                          <div className="loading-container">
                            <Spin
                              size="large"
                              tip={t("resources.loading.news")}
                            />
                          </div>
                        ) : errorNoticiasRHA ? (
                          <Alert
                            message={errorNoticiasRHA}
                            type="error"
                            showIcon
                          />
                        ) : (
                          <Row gutter={[24, 24]} className="noticias-grid">
                            {paginatedNoticiasRHA.map((noticia) => (
                              <Col xs={24} key={noticia.id}>
                                <div className="noticia-card">
                                  <div className="noticia-image">
                                    <img
                                      src={noticia.image}
                                      alt={noticia.title}
                                      crossOrigin="anonymous"
                                    />
                                    {/* <div className="noticia-categoria">{noticia.categoria}</div> */}
                                  </div>
                                  <div className="noticia-content">
                                    <div className="noticia-data">
                                      {noticia.data}
                                    </div>
                                    <h4 className="noticia-title">
                                      {noticia.title}
                                    </h4>
                                    <p className="noticia-description">
                                      {noticia.description}
                                    </p>
                                    <a
                                      href={noticia.link}
                                      className="noticia-link"
                                    >
                                      {t("resources.news.readMore")} →
                                    </a>
                                  </div>
                                </div>
                              </Col>
                            ))}
                          </Row>
                        )}
                        {/* FAQ para RHA no mobile */}
                        {activeNewsAccordion === "rha" && (
                          <div className="faq-mobile-section">
                            <h3 style={{ marginTop: 24 }}>
                              {t("resources.faq.title")}
                            </h3>
                            {loadingPerguntasRHA ? (
                              <div className="loading-container">
                                <Spin
                                  size="large"
                                  tip={t("resources.loading.faq")}
                                />
                              </div>
                            ) : errorPerguntasRHA ? (
                              <Alert
                                message={errorPerguntasRHA}
                                type="error"
                                showIcon
                              />
                            ) : (
                              <div className="faq-container">
                                {perguntasRHAParaMostrar.map((faq) => (
                                  <div
                                    key={faq.id}
                                    className={`faq-item ${
                                      activeFaq === faq.id ? "active" : ""
                                    }`}
                                  >
                                    <div
                                      className="faq-question"
                                      onClick={() =>
                                        setActiveFaq(
                                          activeFaq === faq.id ? null : faq.id
                                        )
                                      }
                                    >
                                      <h4>{faq.pergunta}</h4>
                                      <div className="faq-icon">
                                        <span
                                          className={`faq-arrow ${
                                            activeFaq === faq.id
                                              ? "rotated"
                                              : ""
                                          }`}
                                        >
                                          ▼
                                        </span>
                                      </div>
                                    </div>
                                    <div
                                      className={`faq-answer ${
                                        activeFaq === faq.id ? "expanded" : ""
                                      }`}
                                    >
                                      <div className="faq-answer-content">
                                        <p>{faq.resposta}</p>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <div className="plataforma-tabs">
                    <div
                      className={`tab-item ${
                        activeTab === "csap" ? "active" : ""
                      }`}
                      onClick={() => setActiveTab("csap")}
                    >
                      <h3>{t("resources.tabs.csap")}</h3>
                    </div>
                    <div
                      className={`tab-item ${
                        activeTab === "rha" ? "active" : ""
                      }`}
                      onClick={() => setActiveTab("rha")}
                    >
                      <h3>{t("resources.tabs.rha")}</h3>
                    </div>
                  </div>
                  <div className="plataforma-details">
                    {activeTab === "csap" && (
                      <div className="noticias-content">
                        <div className="noticias-header">
                          <h3>{t("resources.news.csap.title")}</h3>
                          <p>{t("resources.news.csap.subtitle")}</p>
                        </div>
                        {loadingNoticiasCSAP ? (
                          <div className="loading-container">
                            <Spin
                              size="large"
                              tip={t("resources.loading.news")}
                            />
                          </div>
                        ) : errorNoticiasCSAP ? (
                          <Alert
                            message={errorNoticiasCSAP}
                            type="error"
                            showIcon
                          />
                        ) : (
                          <>
                            <Row gutter={[24, 24]} className="noticias-grid">
                              {paginatedNoticiasCSAP.map((noticia) => (
                                <Col xs={24} md={12} lg={8} key={noticia.id}>
                                  <div className="noticia-card">
                                    <div className="noticia-image">
                                      <img
                                        src={noticia.image}
                                        alt={noticia.title}
                                        crossOrigin="anonymous"
                                      />
                                      {/* <div className="noticia-categoria">{noticia.categoria}</div> */}
                                    </div>
                                    <div className="noticia-content">
                                      <div className="noticia-data">
                                        {noticia.data}
                                      </div>
                                      <h4 className="noticia-title">
                                        {noticia.title}
                                      </h4>
                                      <p className="noticia-description">
                                        {noticia.description}
                                      </p>
                                      <a
                                        href={noticia.link}
                                        className="noticia-link"
                                      >
                                        {t("resources.news.readMore")} →
                                      </a>
                                    </div>
                                  </div>
                                </Col>
                              ))}
                            </Row>
                            {noticiasCSAPParaMostrar.length >
                              ITEMS_PER_PAGE && (
                              <div
                                className="pagination-container"
                                style={{
                                  textAlign: "center",
                                  marginTop: "30px",
                                }}
                              >
                                <Pagination
                                  current={currentPageCSAP}
                                  total={noticiasCSAPParaMostrar.length}
                                  pageSize={ITEMS_PER_PAGE}
                                  onChange={handlePageChangeCSAP}
                                  showSizeChanger={false}
                                />
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}
                    {activeTab === "rha" && (
                      <div className="noticias-content">
                        <div className="noticias-header">
                          <h3>{t("resources.news.rha.title")}</h3>
                          <p>{t("resources.news.rha.subtitle")}</p>
                        </div>
                        {loadingNoticiasRHA ? (
                          <div className="loading-container">
                            <Spin
                              size="large"
                              tip={t("resources.loading.news")}
                            />
                          </div>
                        ) : errorNoticiasRHA ? (
                          <Alert
                            message={errorNoticiasRHA}
                            type="error"
                            showIcon
                          />
                        ) : (
                          <>
                            <Row gutter={[24, 24]} className="noticias-grid">
                              {paginatedNoticiasRHA.map((noticia) => (
                                <Col xs={24} md={12} lg={8} key={noticia.id}>
                                  <div className="noticia-card">
                                    <div className="noticia-image">
                                      <img
                                        src={noticia.image}
                                        alt={noticia.title}
                                        crossOrigin="anonymous"
                                      />
                                      {/* <div className="noticia-categoria">{noticia.categoria}</div> */}
                                    </div>
                                    <div className="noticia-content">
                                      <div className="noticia-data">
                                        {noticia.data}
                                      </div>
                                      <h4 className="noticia-title">
                                        {noticia.title}
                                      </h4>
                                      <p className="noticia-description">
                                        {noticia.description}
                                      </p>
                                      <a
                                        href={noticia.link}
                                        className="noticia-link"
                                      >
                                        {t("resources.news.readMore")} →
                                      </a>
                                    </div>
                                  </div>
                                </Col>
                              ))}
                            </Row>
                            {noticiasRHAParaMostrar.length > ITEMS_PER_PAGE && (
                              <div
                                className="pagination-container"
                                style={{
                                  textAlign: "center",
                                  marginTop: "30px",
                                }}
                              >
                                <Pagination
                                  current={currentPageRHA}
                                  total={noticiasRHAParaMostrar.length}
                                  pageSize={ITEMS_PER_PAGE}
                                  onChange={handlePageChangeRHA}
                                  showSizeChanger={false}
                                />
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </>
              )}
            </Card>
          </div>
        </div>
      </section>
      {/* Seção FAQ (desktop) */}
      {!isMobile && (
        <section id="faq-section" className="faq-section" ref={faqSectionRef}>
          <div className="container">
            <div className="faq-header">
              <h2>{t("resources.faq.title")}</h2>
              <p>{t("resources.faq.subtitle")}</p>
            </div>
            {activeTab === "rha" ? (
              loadingPerguntasRHA ? (
                <div className="loading-container">
                  <Spin size="large" tip={t("resources.loading.faq")} />
                </div>
              ) : errorPerguntasRHA ? (
                <Alert message={errorPerguntasRHA} type="error" showIcon />
              ) : (
                <div className="faq-container">
                  {perguntasRHAParaMostrar.map((faq) => (
                    <div
                      key={faq.id}
                      className={`faq-item ${
                        activeFaq === faq.id ? "active" : ""
                      }`}
                    >
                      <div
                        className="faq-question"
                        onClick={() =>
                          setActiveFaq(activeFaq === faq.id ? null : faq.id)
                        }
                      >
                        <h4>{faq.pergunta}</h4>
                        <div className="faq-icon">
                          <span
                            className={`faq-arrow ${
                              activeFaq === faq.id ? "rotated" : ""
                            }`}
                          >
                            ▼
                          </span>
                        </div>
                      </div>
                      <div
                        className={`faq-answer ${
                          activeFaq === faq.id ? "expanded" : ""
                        }`}
                      >
                        <div className="faq-answer-content">
                          <p>{faq.resposta}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )
            ) : loadingPerguntasCSAP ? (
              <div className="loading-container">
                <Spin size="large" tip={t("resources.loading.faq")} />
              </div>
            ) : errorPerguntasCSAP ? (
              <Alert message={errorPerguntasCSAP} type="error" showIcon />
            ) : (
              <div className="faq-container">
                {perguntasCSAPParaMostrar.map((faq) => (
                  <div
                    key={faq.id}
                    className={`faq-item ${
                      activeFaq === faq.id ? "active" : ""
                    }`}
                  >
                    <div
                      className="faq-question"
                      onClick={() =>
                        setActiveFaq(activeFaq === faq.id ? null : faq.id)
                      }
                    >
                      <h4>{faq.pergunta}</h4>
                      <div className="faq-icon">
                        <span
                          className={`faq-arrow ${
                            activeFaq === faq.id ? "rotated" : ""
                          }`}
                        >
                          ▼
                        </span>
                      </div>
                    </div>
                    <div
                      className={`faq-answer ${
                        activeFaq === faq.id ? "expanded" : ""
                      }`}
                    >
                      <div className="faq-answer-content">
                        <p>{faq.resposta}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}
      <Contact />
      <Footer />
    </div>
  );
};

export default Recursos;
